import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../utils/api';
import { toast } from 'sonner';

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('title');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState({});

  useEffect(() => {
    api.get('/templates')
      .then((res) => setTemplates(res.data || []))
      .catch(() => toast.error('Error al cargar plantillas'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return templates.filter((tmpl) => {
      if (!normalized) return true;
      return tmpl.title?.toLowerCase().includes(normalized);
    });
  }, [templates, query]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      const aValue = (a[sortKey] || '').toString().toLowerCase();
      const bValue = (b[sortKey] || '').toString().toLowerCase();
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return copy;
  }, [filtered, sortDir, sortKey]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const allSelected = sorted.length > 0 && sorted.every((t) => selected[t.id]);
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const next = {};
    sorted.forEach((tmpl) => {
      next[tmpl.id] = true;
    });
    setSelected(next);
  };
  const toggleRow = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleExport = () => {
    const rows = sorted.filter((t) => selected[t.id]);
    if (!rows.length) {
      toast.error('Selecciona al menos una plantilla');
      return;
    }
    const header = ['title', 'cadence', 'sla_hours'];
    const csv = [
      header.join(','),
      ...rows.map((t) => [t.title, t.cadence, t.sla_hours].map((v) => `"${v || ''}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'plantillas.csv';
    link.click();
  };

  return (
    <div data-testid="templates-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-4xl font-heading font-bold">Plantillas de Tareas</h1>
          <p className="text-muted-foreground">Estandariza tareas recurrentes</p>
        </div>
        <Button variant="outline" onClick={handleExport}>Exportar CSV</Button>
      </div>
      <Card className="mb-4">
        <CardContent className="p-4">
          <Input
            placeholder="Buscar por titulo"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium cursor-pointer" onClick={() => toggleSort('title')}>
                  Titulo
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium cursor-pointer" onClick={() => toggleSort('cadence')}>
                  Cadencia
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium cursor-pointer" onClick={() => toggleSort('sla_hours')}>
                  SLA (horas)
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" className="px-6 py-4 text-center">Cargando...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="4" className="px-6 py-4 text-center">No hay plantillas</td></tr>
              ) : (
                sorted.map((tmpl) => (
                  <tr key={tmpl.id} className="border-t">
                    <td className="px-4 py-4">
                      <input type="checkbox" checked={Boolean(selected[tmpl.id])} onChange={() => toggleRow(tmpl.id)} />
                    </td>
                    <td className="px-6 py-4">{tmpl.title}</td>
                    <td className="px-6 py-4">{tmpl.cadence}</td>
                    <td className="px-6 py-4">{tmpl.sla_hours}h</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};
export default Templates;
